/**
 * Tile for a PDF that lives in a photo grid (a drukattest straight off the
 * pressure-test device, for example).
 *
 * A PDF has no thumbnail — rendering it through `<img>` gives a broken image —
 * and it must not open in `PhotoLightbox`, which is an image zoom/pan viewer.
 * So it renders as a link that opens the file in a new tab; the photo proxy
 * serves `application/pdf` inline, so the browser shows it directly.
 *
 * Prefer `PhotoGridThumb`, which picks between this and an `<img>` for you.
 */
export function PdfPhotoTile({
  url,
  filename,
  variant = 'grid',
  sizeClass,
}: {
  url: string;
  filename: string;
  variant?: 'grid' | 'strip';
  /** Overrides the default box size so the tile matches the grid it sits in. */
  sizeClass?: string;
}) {
  const icon = (
    <svg
      className={variant === 'grid' ? 'w-7 h-7 text-red-500' : 'w-4 h-4 text-red-500 shrink-0'}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );

  const common =
    'flex items-center justify-center bg-red-50 border border-red-200 hover:border-td-blue transition-colors';

  if (variant === 'strip') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${common} ${sizeClass ?? 'h-10 max-w-[9rem]'} gap-1 px-2 rounded shrink-0`}
        aria-label={`PDF ${filename} openen`}
        title={filename}
      >
        {icon}
        <span className="text-[10px] font-semibold text-red-700 truncate">{filename}</span>
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`${common} ${sizeClass ?? 'w-20 h-20'} flex-col gap-0.5 p-1 rounded-lg`}
      aria-label={`PDF ${filename} openen`}
      title={filename}
    >
      {icon}
      <span className="text-[9px] font-semibold text-red-700 leading-tight text-center line-clamp-2 break-all">
        {filename}
      </span>
    </a>
  );
}
